// EL_ListFamilyUsers.tsx
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Loading from "../Common/Loading";

// Import images
import addIcon from "../Images/add_d.gif";
import editIcon from "../Images/edit_o.gif";
import deleteIcon from "../Images/delete_d.gif";
import cancelIcon from "../Images/cancel_d.gif";

const API_BASE = process.env.REACT_APP_DOCUMENT_LINK;

interface FamilyUser {
  // Keep these internally because Edit/Delete use them.
  // They will NOT be displayed in the table.
  SchoolCode: string;
  FamilyCode: string;
  Login: string;

  UserAccountType: string;
  FName: string;
  LName: string;
  Gender: string;
  DOB: string;
  Std: string;
  Competency: string;
  CanAccessLessonPlan: string;
  CanEarnPoints: string;
  Curriculum: string;
}

interface Column {
  name: string;
  label: string;
  options?: Record<string, string>;
}

export default function EL_ListFamilyUsers() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [familyName, setFamilyName] = useState("");
  const [data, setData] = useState<FamilyUser[]>([]);
  const [columns, setColumns] = useState<Column[]>([]);
  const [maxChildren, setMaxChildren] = useState(0);
  const [currentChildren, setCurrentChildren] = useState(0);
  const [canAdd, setCanAdd] = useState(false);
  const [selectedRow, setSelectedRow] = useState<FamilyUser | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  // const schoolCode = searchParams.get("SchoolCode") || "";
  // const familyLogin = searchParams.get("Login") || "";
  // const createdFromNewYLA = searchParams.get("CreatedFromNewYLA") || "";

  const schoolCode = "DLR_KE_AU";
  const supportSchoolCode =
    sessionStorage.getItem("SelectedSchoolCode") || "";
  const familyLogin = searchParams.get("Login");
  const createdFromNewYLA =
    searchParams.get("CreatedFromNewYLA") || "";

  // const schoolCode = sessionStorage.getItem("SelectedSchoolCode") || "";
  // const familyLogin = sessionStorage.getItem("L") || "";
  // const createdFromNewYLA = searchParams.get("CreatedFromNewYLA") || "";

  const fetchData = async () => {
    setLoading(true);

    try {
      const response = await fetch(
        `${API_BASE}/api/list-family-users?SchoolCode=${supportSchoolCode}&Login=${familyLogin}&CreatedFromNewYLA=${createdFromNewYLA}`
      );

      const json = await response.json();

      setFamilyName(json.family_name);

      /*
       * Keep SchoolCode, FamilyCode and Login in the data object
       * because Edit and Delete still require them.
       *
       * They are only removed from the displayed columns below.
       */
      const formattedData: FamilyUser[] = json.data.map(
        (row: any[]) => ({
          SchoolCode: row[0],
          FamilyCode: row[1],
          Login: row[2],

          UserAccountType: row[3],
          FName: row[4],
          LName: row[5],
          Gender: row[6],
          DOB: row[7],
          Std: row[8],
          Competency: row[9] == null ? "" : String(row[9]),
          CanAccessLessonPlan: row[10],
          CanEarnPoints: row[11],
          Curriculum: row[12],
        })
      );

      setData(formattedData);

      /*
       * Add "#" as the first column.
       *
       * Remove:
       * - SchoolCode
       * - FamilyCode
       * - Login
       *
       * from the displayed table only.
       */
      setColumns([
        {
          name: "serialNo",
          label: "#",
        },
        ...json.columns.filter(
          (col: Column) =>
            col.name !== "SchoolCode" &&
            col.name !== "FamilyCode" &&
            col.name !== "Login"
        ),
      ]);

      setMaxChildren(json.max_children);
      setCurrentChildren(json.current_children);
      setCanAdd(json.can_add);
    } catch (error) {
      console.error("Error fetching data:", error);
      alert("Error loading data. Check console for details.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.body.style.backgroundColor = "azure";

    fetchData();
  }, [schoolCode, familyLogin, createdFromNewYLA, supportSchoolCode]);

  // --- Button Handlers ---

  const handleAdd = () => {
    if (!canAdd) return;

    navigate(
      `/DEALER/EL_AddEditFamilyUser?SchoolCode=${supportSchoolCode}&FamilyCode=${familyLogin}&CreatedFromNewYLA=${createdFromNewYLA}`
    );
  };

  const handleEdit = () => {
    if (!selectedRow) {
      alert("Please select a child");
      return;
    }

    navigate(
      `/DEALER/EL_AddEditFamilyUser?SchoolCode=${selectedRow.SchoolCode}&FamilyCode=${selectedRow.FamilyCode}&Login=${selectedRow.Login}`
    );
  };

  const handleDeleteClick = () => {
    if (!selectedRow) {
      alert("Please select a child");
      return;
    }

    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    setLoading(true);

    try {
      const response = await fetch(
        `${API_BASE}/api/delete-child`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            SchoolCode: selectedRow?.SchoolCode,
            FamilyCode: selectedRow?.FamilyCode,
            Login: selectedRow?.Login,
          }),
        }
      );

      const json = await response.json();

      if (json.success) {
        fetchData();
        setSelectedRow(null);
      } else {
        alert("Failed to delete child: " + json.error);
      }
    } catch (error) {
      console.error("Delete error:", error);
      alert("Error deleting child. Check console.");
    } finally {
      setShowDeleteConfirm(false);
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(
      `/DEALER/EL_ListFamilies?SchoolCode=${supportSchoolCode}`
    );
  };

  const formatValue = (
    value: string,
    column: Column
  ) => {
    if (column.options && column.options[value]) {
      return column.options[value];
    }

    return value;
  };

  // Helper function to handle image hover effects
  const getImageProps = (
    defaultSrc: string,
    hoverSrc: string
  ) => ({
    src: defaultSrc,

    onMouseOver: (
      e: React.MouseEvent<HTMLImageElement>
    ) => {
      e.currentTarget.src = hoverSrc;
    },

    onMouseOut: (
      e: React.MouseEvent<HTMLImageElement>
    ) => {
      e.currentTarget.src = defaultSrc;
    },

    onMouseDown: (
      e: React.MouseEvent<HTMLImageElement>
    ) => {
      e.currentTarget.src = hoverSrc;
    },

    style: {
      cursor: "pointer",
    },
  });

  return (
    <div style={{ padding: 5 }}>
      <h3
        style={{
          textAlign: "center",
          color: "navy",
          fontFamily: "Arial",
          margin: 0,
        }}
      >
        {supportSchoolCode}
      </h3>

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: "2%",
          marginBottom: "5px",
          marginRight: "5px",
          marginTop: "0px",
        }}
      >
        <a
          href="/SUPPORT/DealerDashboard"
          style={{
            textDecoration: "underline",
            color: "blue",
            fontWeight: "bolder",
            fontFamily: "Arial",
            fontSize: "14px",
          }}
        >
          Back To Menu
        </a>

        <a
          href="/SUPPORT/SupportLoggedOut"
          style={{
            textDecoration: "underline",
            color: "blue",
            fontWeight: "bolder",
            fontFamily: "Arial",
            fontSize: "14px",
          }}
        >
          Logout
        </a>
      </div>

      <div
        style={{
          color: "black",
          textAlign: "center",
        }}
      >
        Student Details of Family: <b>{familyName}</b>
      </div>

      {/* Scrollable Table */}
      <div
        style={{
          margin: "20px 0",
          height: 275,
          overflowY: "auto",
          border: "1px solid #80a0ff",
          width: "calc(100vw - 40px)",
        }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            borderBottom: "1px solid #80a0ff",
          }}
        >
          <thead>
            <tr
              style={{
                backgroundColor: "#c7ddfd",
              }}
            >
              {columns.map((col) => (
                <th
                  key={col.name}
                  style={{
                    padding: 8,
                    textAlign: "left",
                    border: "1px solid #80a0ff",
                    position: "sticky",
                    top: 0,
                    background: "#c7ddfd",
                    color: "#2859ac",
                    fontWeight: "normal",
                    fontSize: "10pt",
                    fontFamily: "Arial",
                  }}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {data.map((row, index) => (
              <tr
                key={index}
                onClick={() => setSelectedRow(row)}
                style={{
                  backgroundColor:
                    selectedRow?.Login === row.Login
                      ? "orange"
                      : index % 2 === 0
                      ? "#f4faff"
                      : "#ffffff",

                  color:
                    selectedRow?.Login === row.Login
                      ? "white"
                      : "black",

                  cursor: "pointer",
                  fontSize: "10pt",
                  fontFamily: "Arial",
                }}
              >
                {columns.map((col) => (
                  <td
                    key={col.name}
                    style={{
                      padding: 8,
                      borderLeft:
                        "1px solid #80a0ff",
                    }}
                  >
                    {col.name === "serialNo"
                      ? index + 1
                      : formatValue(
                          row[
                            col.name as keyof FamilyUser
                          ],
                          col
                        )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Action Buttons */}
      <table
        style={{
          width: "30%",
          margin: "20px auto",
        }}
      >
        <tbody>
          <tr>
            {/* Add Button */}
            <td
              style={{
                display: canAdd
                  ? "table-cell"
                  : "none",
                textAlign: "center",
              }}
            >
              <img
                {...getImageProps(
                  addIcon,
                  addIcon
                )}
                alt="Add"
                onClick={handleAdd}
              />
            </td>

            {/* Edit Button */}
            <td
              style={{
                display: selectedRow
                  ? "table-cell"
                  : "none",
                textAlign: "center",
              }}
            >
              <img
                {...getImageProps(
                  editIcon,
                  editIcon
                )}
                alt="Edit"
                onClick={handleEdit}
              />
            </td>

            {/* Delete Button */}
            <td
              style={{
                display: selectedRow
                  ? "table-cell"
                  : "none",
                textAlign: "center",
              }}
            >
              <img
                {...getImageProps(
                  deleteIcon,
                  deleteIcon
                )}
                alt="Delete"
                onClick={handleDeleteClick}
              />
            </td>

            {/* Cancel Button */}
            <td
              style={{
                textAlign: "center",
              }}
            >
              <img
                {...getImageProps(
                  cancelIcon,
                  cancelIcon
                )}
                alt="Cancel"
                onClick={handleCancel}
              />
            </td>
          </tr>
        </tbody>
      </table>

      {loading && <Loading />}

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor:
              "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: "white",
              padding: 20,
              borderRadius: 5,
              boxShadow:
                "0 2px 10px rgba(0,0,0,0.2)",
            }}
          >
            <h3>Confirm Delete</h3>

            <p>
              Are you sure you want to delete{" "}
              {selectedRow?.Login}?
            </p>

            <div
              style={{
                textAlign: "right",
                marginTop: 20,
              }}
            >
              <button
                onClick={() =>
                  setShowDeleteConfirm(false)
                }
                style={{
                  marginRight: 10,
                }}
              >
                Cancel
              </button>

              <button
                onClick={handleDeleteConfirm}
                style={{
                  backgroundColor: "#f44336",
                  color: "white",
                  border: "none",
                  padding: "5px 15px",
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}